import React from "react";
import { CheckCircle, Circle, Clock } from "lucide-react";
import type { QuestionGroup } from "@/types/database";

interface GroupProgressBreadcrumbProps {
  questionGroups: QuestionGroup[];
  currentGroupIndex: number;
}

export const GroupProgressBreadcrumb = React.memo(
  ({ questionGroups, currentGroupIndex }: GroupProgressBreadcrumbProps) => {
    const getGroupStatus = (index: number) => {
      if (index < currentGroupIndex) return "completed";
      if (index === currentGroupIndex) return "current";
      return "upcoming";
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case "completed":
          return <CheckCircle className="h-4 w-4 text-green-500" />;
        case "current":
          return <Clock className="h-4 w-4 text-blue-500" />;
        default:
          return <Circle className="h-4 w-4 text-gray-300" />;
      }
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case "completed":
          return "text-green-600 bg-green-50 border-green-200";
        case "current":
          return "text-blue-600 bg-blue-50 border-blue-200";
        default:
          return "text-gray-500 bg-gray-50 border-gray-200";
      }
    };

    return (
      <div className="mb-6">
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {questionGroups.map((group, index) => {
            const status = getGroupStatus(index);
            return (
              <React.Fragment key={group.id}>
                <div
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${getStatusColor(
                    status
                  )}`}
                >
                  {getStatusIcon(status)}
                  <span>{group.name}</span>
                </div>
                {index < questionGroups.length - 1 && (
                  <div className="h-px w-4 bg-gray-300" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }
);

GroupProgressBreadcrumb.displayName = "GroupProgressBreadcrumb";
